import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Check, X, NotePencil } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import LicenseTab from '@/components/settings/LicenseTab';

// ADM-26: fields for the Organization settings form, grouped exactly as the
// backend's OrganizationUpdate model expects (all sent back together on save —
// the backend model has everything Optional so a full-object PUT is safe).
const ORG_EMPTY_FORM = {
  name: '', legal_name: '', logo_url: '', website: '',
  gst_registered: false, gstin: '', pan: '', state: '', state_code: '', pincode: '',
  address_line1: '', address_line2: '', city: '',
  phone: '', email: '',
  invoice_prefix: '', authorized_signatory_name: '',
  bank_account_name: '', bank_account_number: '', bank_ifsc: '', bank_name: '', bank_branch: '',
};

function OrgSectionHeader({ children }) {
  return <h4 className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mt-5 mb-2.5 first:mt-0">{children}</h4>;
}

function OrgField({ label, field, form, setForm, editable, type = 'text', className = '' }) {
  return (
    <div className={className}>
      <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">{label}</Label>
      <Input
        data-testid={`org-field-${field}`}
        type={type}
        value={form[field] ?? ''}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
        disabled={!editable}
        className="h-9 rounded-lg text-sm mt-1 disabled:opacity-70 disabled:cursor-default"
      />
    </div>
  );
}

function OrganizationTab({ user, branchesCount, usersCount }) {
  const isSuperAdmin = user?.role === 'super_admin';
  const [form, setForm] = useState(ORG_EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const fetchOrg = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/settings/organization');
      setForm({ ...ORG_EMPTY_FORM, ...data });
    } catch (e) {
      console.error(e);
      toast.error('Failed to load organization settings');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchOrg(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put('/api/settings/organization', form);
      setForm({ ...ORG_EMPTY_FORM, ...data });
      toast.success('Organization settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div>;

  return (
    <Card className="border border-border rounded-xl max-w-3xl"><CardContent className="p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display text-sm font-bold text-foreground">Organization</h3>
        <div className="text-[11px] text-muted-foreground font-mono">{branchesCount} branches &middot; {usersCount} users</div>
      </div>
      <form onSubmit={handleSave} data-testid="org-settings-form">
        <OrgSectionHeader>Brand</OrgSectionHeader>
        <div className="grid grid-cols-2 gap-3">
          <OrgField label="Name" field="name" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="Legal Name" field="legal_name" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="Website" field="website" form={form} setForm={setForm} editable={isSuperAdmin} />
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Logo URL</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                data-testid="org-field-logo_url"
                value={form.logo_url ?? ''}
                onChange={e => { setForm(f => ({ ...f, logo_url: e.target.value })); setLogoError(false); }}
                disabled={!isSuperAdmin}
                className="h-9 rounded-lg text-sm disabled:opacity-70 disabled:cursor-default"
              />
              {form.logo_url && !logoError && (
                <img src={form.logo_url} alt="Logo preview" className="h-9 w-9 rounded-lg object-contain border border-border bg-secondary/40 shrink-0" onError={() => setLogoError(true)} />
              )}
            </div>
          </div>
        </div>

        <OrgSectionHeader>Tax & Registration</OrgSectionHeader>
        <div className="flex items-center gap-2 mb-3">
          <Checkbox
            id="org-gst-registered"
            data-testid="org-field-gst_registered"
            checked={!!form.gst_registered}
            onCheckedChange={(v) => setForm(f => ({ ...f, gst_registered: !!v }))}
            disabled={!isSuperAdmin}
          />
          <Label htmlFor="org-gst-registered" className="text-xs font-normal text-foreground">GST Registered</Label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <OrgField label="GSTIN" field="gstin" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="PAN" field="pan" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="State" field="state" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="State Code" field="state_code" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="Pincode" field="pincode" form={form} setForm={setForm} editable={isSuperAdmin} />
        </div>

        <OrgSectionHeader>Registered Address</OrgSectionHeader>
        <div className="grid grid-cols-2 gap-3">
          <OrgField label="Address Line 1" field="address_line1" form={form} setForm={setForm} editable={isSuperAdmin} className="col-span-2" />
          <OrgField label="Address Line 2" field="address_line2" form={form} setForm={setForm} editable={isSuperAdmin} className="col-span-2" />
          <OrgField label="City" field="city" form={form} setForm={setForm} editable={isSuperAdmin} />
        </div>

        <OrgSectionHeader>Contact</OrgSectionHeader>
        <div className="grid grid-cols-2 gap-3">
          <OrgField label="Phone" field="phone" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="Email" field="email" form={form} setForm={setForm} editable={isSuperAdmin} />
        </div>

        <OrgSectionHeader>Invoice Settings</OrgSectionHeader>
        <div className="grid grid-cols-2 gap-3">
          <OrgField label="Invoice Prefix" field="invoice_prefix" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="Authorized Signatory Name" field="authorized_signatory_name" form={form} setForm={setForm} editable={isSuperAdmin} />
        </div>

        <p className="text-[10px] text-muted-foreground mt-4 mb-2">Bank Details (shown on invoices for cash/cheque/bank-transfer payments)</p>
        <div className="grid grid-cols-2 gap-3">
          <OrgField label="Account Name" field="bank_account_name" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="Account Number" field="bank_account_number" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="IFSC" field="bank_ifsc" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="Bank Name" field="bank_name" form={form} setForm={setForm} editable={isSuperAdmin} />
          <OrgField label="Branch" field="bank_branch" form={form} setForm={setForm} editable={isSuperAdmin} />
        </div>

        {isSuperAdmin && (
          <Button type="submit" disabled={saving} data-testid="org-settings-save-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white mt-5 disabled:opacity-60" style={{ backgroundColor: '#E04B2A' }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </form>
    </CardContent></Card>
  );
}

const ROLES = ['super_admin', 'branch_manager', 'counselor', 'front_desk', 'finance', 'faculty'];
const ROLE_COLOR = { super_admin: '#EF4444', branch_manager: '#8B5CF6', counselor: '#2B6EF0', front_desk: '#10A882', finance: '#E5A100', faculty: '#06B6D4' };
const ROLE_LABELS = { super_admin: 'Super Admin', branch_manager: 'Branch Manager', counselor: 'Counselor', front_desk: 'Front Desk', finance: 'Finance', faculty: 'Faculty' };

const PERMISSIONS = [
  { section: 'Dashboard', permission: 'View dashboard & KPIs', super_admin: true, branch_manager: true, counselor: true, front_desk: true, finance: true, faculty: false },
  { section: 'Leads', permission: 'View leads', super_admin: true, branch_manager: true, counselor: 'own', front_desk: true, finance: false, faculty: false },
  { section: 'Leads', permission: 'Create / edit leads', super_admin: true, branch_manager: true, counselor: true, front_desk: false, finance: false, faculty: false },
  { section: 'Leads', permission: 'Delete leads', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Leads', permission: 'Import / export CSV', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Pipeline', permission: 'View & move leads', super_admin: true, branch_manager: true, counselor: true, front_desk: false, finance: false, faculty: false },
  { section: 'Pipeline', permission: 'Bulk stage changes', super_admin: true, branch_manager: true, counselor: true, front_desk: false, finance: false, faculty: false },
  { section: 'Fees', permission: 'View invoices', super_admin: true, branch_manager: true, counselor: true, front_desk: true, finance: true, faculty: false },
  { section: 'Fees', permission: 'Create invoices', super_admin: true, branch_manager: true, counselor: true, front_desk: false, finance: true, faculty: false },
  { section: 'Fees', permission: 'Edit invoices', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: true, faculty: false },
  { section: 'Fees', permission: 'Record payments', super_admin: true, branch_manager: true, counselor: true, front_desk: true, finance: true, faculty: false },
  { section: 'Communication', permission: 'Send messages', super_admin: true, branch_manager: true, counselor: true, front_desk: false, finance: false, faculty: false },
  { section: 'Communication', permission: 'Bulk campaigns', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Communication', permission: 'WhatsApp config', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Automation', permission: 'Create / manage rules', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Reports', permission: 'View analytics', super_admin: true, branch_manager: true, counselor: true, front_desk: true, finance: true, faculty: false },
  { section: 'Reports', permission: 'Export reports', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Branches', permission: 'Create branches', super_admin: true, branch_manager: false, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Branches', permission: 'Edit branches', super_admin: true, branch_manager: 'own', counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Settings', permission: 'Manage users & roles', super_admin: true, branch_manager: false, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Settings', permission: 'Pipeline stage config', super_admin: true, branch_manager: false, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Audit Logs', permission: 'View audit logs', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
];

function PermCell({ value }) {
  if (value === true) return <Check size={14} weight="bold" className="text-emerald-500 mx-auto" />;
  if (value === 'own') return <span className="text-[9px] font-bold text-amber-500 uppercase">Own</span>;
  return <X size={12} weight="bold" className="text-muted-foreground/25 mx-auto" />;
}

function RolesPermissionsTable() {
  let lastSection = '';
  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden">
      <Table data-testid="roles-permissions-table">
        <TableHeader>
          <TableRow className="bg-secondary/50 hover:bg-secondary/50">
            <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider w-[100px]">Section</TableHead>
            <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Permission</TableHead>
            {ROLES.map(r => (
              <TableHead key={r} className="text-[10px] font-bold h-9 uppercase tracking-wider text-center w-[90px]" style={{ color: ROLE_COLOR[r] }}>{ROLE_LABELS[r]}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {PERMISSIONS.map((p, i) => {
            const showSection = p.section !== lastSection;
            lastSection = p.section;
            return (
              <TableRow key={i} className={showSection ? 'border-t-2 border-border' : ''}>
                <TableCell className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider py-2">{showSection ? p.section : ''}</TableCell>
                <TableCell className="text-[11px] text-foreground py-2">{p.permission}</TableCell>
                {ROLES.map(r => (
                  <TableCell key={r} className="text-center py-2"><PermCell value={p[r]} /></TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'counselor', phone: '', branch_ids: [] });
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', role: 'counselor', branch_ids: [] });
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchAll = async () => {
    try { const [u, b] = await Promise.all([api.get('/api/users'), api.get('/api/branches')]); setUsers(u.data.users || []); setBranches(b.data.branches || []); } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/users', form);
      toast.success('User created');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', role: 'counselor', phone: '', branch_ids: [] });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleStatusToggle = async (u) => {
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/api/users/${u.id}`, { status: newStatus });
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchAll();
    } catch (err) { toast.error('Failed to update status'); }
  };

  const toggleBranchInForm = (branchId) => {
    setForm(prev => {
      const ids = prev.branch_ids.includes(branchId)
        ? prev.branch_ids.filter(id => id !== branchId)
        : [...prev.branch_ids, branchId];
      return { ...prev, branch_ids: ids };
    });
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setEditForm({ name: u.name || '', email: u.email || '', phone: u.phone || '', role: u.role || 'counselor', branch_ids: u.branch_ids || [] });
  };

  const toggleBranchInEditForm = (branchId) => {
    setEditForm(prev => {
      const ids = prev.branch_ids.includes(branchId)
        ? prev.branch_ids.filter(id => id !== branchId)
        : [...prev.branch_ids, branchId];
      return { ...prev, branch_ids: ids };
    });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setSavingEdit(true);
    try {
      await api.put(`/api/users/${editingUser.id}`, editForm);
      toast.success('User updated');
      setEditingUser(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div>;

  // Sidebar already hides this link from non-super_admin -- this is the
  // defense-in-depth check for anyone navigating here directly by URL.
  if (user?.role !== 'super_admin') {
    return (
      <div data-testid="settings-access-denied" className="text-sm text-muted-foreground text-center py-24">
        You don't have access to this page.
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="stagger-in">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">User management & organization</p>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-secondary rounded-lg h-9"><TabsTrigger value="users" data-testid="tab-users" className="text-xs rounded-md font-mono">Users ({users.length})</TabsTrigger><TabsTrigger value="roles" data-testid="tab-roles" className="text-xs rounded-md font-mono">Roles & Access</TabsTrigger><TabsTrigger value="org" data-testid="tab-org" className="text-xs rounded-md font-mono">Organization</TabsTrigger><TabsTrigger value="license" data-testid="tab-license" className="text-xs rounded-md font-mono">License</TabsTrigger></TabsList>

        <TabsContent value="users" className="mt-4 space-y-3">
          <div className="flex justify-end">
            {user?.role === 'super_admin' && (
              <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogTrigger asChild><Button data-testid="add-user-btn" className="h-9 rounded-lg text-xs gap-1.5 font-bold text-white" style={{ backgroundColor: '#E04B2A' }}><Plus size={14} weight="bold" /> Add User</Button></DialogTrigger>
                <DialogContent className="rounded-xl max-w-md"><DialogHeader><DialogTitle className="font-display text-foreground text-lg">Add User</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-3">
                    <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Name *</Label><Input data-testid="user-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
                    <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Email *</Label><Input data-testid="user-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
                    <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Password *</Label><Input data-testid="user-password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Role</Label>
                        <select data-testid="user-role" value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">{ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}</select></div>
                      <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Branches</Label>
                      <div data-testid="user-branch-select" className="flex flex-wrap gap-1.5 mt-1.5 p-2 border border-border rounded-lg bg-card min-h-[36px]">
                        {branches.map(b => (
                          <button
                            key={b.id}
                            type="button"
                            data-testid={`branch-opt-${b.id}`}
                            onClick={() => toggleBranchInForm(b.id)}
                            className={`text-[10px] px-2 py-1 rounded font-mono transition-colors ${
                              form.branch_ids.includes(b.id)
                                ? 'bg-foreground text-background font-bold'
                                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                            }`}
                          >
                            {b.name}
                          </button>
                        ))}
                        {branches.length === 0 && <span className="text-[10px] text-muted-foreground">No branches</span>}
                      </div>
                    </div>
                    <Button type="submit" data-testid="submit-user-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>Create</Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="border border-border rounded-xl bg-card overflow-hidden">
            <Table><TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Email</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Role</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Phone</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Branches</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider w-[40px]" />
            </TableRow></TableHeader><TableBody>{users.map(u => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="text-[12px] font-semibold py-2.5">{u.name}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5 font-mono">{u.email}</TableCell>
                <TableCell className="py-2.5">
                  <span data-testid={`role-label-${u.id}`} className="text-[10px] font-bold uppercase tracking-wider" style={{ color: ROLE_COLOR[u.role] || '#808080' }}>{u.role?.replace('_', ' ')}</span>
                </TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5 font-mono">{u.phone || '-'}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5 max-w-[200px] truncate">{u.branch_ids?.map(id => branches.find(b => b.id === id)?.name || id).join(', ') || '-'}</TableCell>
                <TableCell className="py-2.5">
                  {user?.role === 'super_admin' ? (
                    <button
                      data-testid={`status-toggle-${u.id}`}
                      onClick={() => handleStatusToggle(u)}
                      className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                        u.status === 'active' ? 'text-emerald-600' : 'text-muted-foreground/50'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full inline-block ${u.status === 'active' ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
                      {u.status || 'active'}
                    </button>
                  ) : (
                    <span className={`w-2 h-2 rounded-full inline-block ${u.status === 'active' ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
                  )}
                </TableCell>
                <TableCell className="py-2.5">
                  {user?.role === 'super_admin' && (
                    <button
                      data-testid={`edit-user-${u.id}`}
                      onClick={() => openEditUser(u)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      title="Edit user"
                    >
                      <NotePencil size={13} />
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}</TableBody></Table>
          </div>

          <Dialog open={!!editingUser} onOpenChange={(open) => { if (!open) setEditingUser(null); }}>
            <DialogContent className="rounded-xl max-w-md" data-testid="edit-user-dialog">
              <DialogHeader><DialogTitle className="font-display text-foreground text-lg">Edit User</DialogTitle></DialogHeader>
              <form onSubmit={handleEditSave} className="space-y-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Name *</Label><Input data-testid="edit-user-name" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="h-9 rounded-lg text-sm" required /></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Email *</Label><Input data-testid="edit-user-email" type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="h-9 rounded-lg text-sm" required /></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Phone</Label><Input data-testid="edit-user-phone" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} className="h-9 rounded-lg text-sm" /></div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Role</Label>
                  <select data-testid="edit-user-role" value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
                    {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Branches</Label>
                  <div data-testid="edit-user-branch-select" className="flex flex-wrap gap-1.5 mt-1.5 p-2 border border-border rounded-lg bg-card min-h-[36px]">
                    {branches.map(b => (
                      <button
                        key={b.id}
                        type="button"
                        data-testid={`edit-branch-opt-${b.id}`}
                        onClick={() => toggleBranchInEditForm(b.id)}
                        className={`text-[10px] px-2 py-1 rounded font-mono transition-colors ${
                          editForm.branch_ids.includes(b.id)
                            ? 'bg-foreground text-background font-bold'
                            : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                        }`}
                      >
                        {b.name}
                      </button>
                    ))}
                    {branches.length === 0 && <span className="text-[10px] text-muted-foreground">No branches</span>}
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground">Status is toggled from the table directly. To reset a password, use the appropriate account-recovery flow.</p>
                <Button type="submit" disabled={savingEdit} data-testid="save-edit-user-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white disabled:opacity-60" style={{ backgroundColor: '#E04B2A' }}>
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <RolesPermissionsTable />
        </TabsContent>

        <TabsContent value="org" className="mt-4">
          <Card className="border border-border rounded-xl max-w-lg"><CardContent className="p-5 space-y-3">
            <h3 className="font-display text-sm font-bold text-foreground">Organization</h3>
            <div className="space-y-2 text-[12px]">
              {[['Name', 'YuHasPro'], ['Branches', branches.length], ['Users', users.length], ['Currency', 'INR'], ['Timezone', 'Asia/Kolkata'], ['Academic Year', '2026-27']].map(([k, v]) => (
                <div key={k} className="flex gap-3"><span className="text-muted-foreground w-28 text-[11px]">{k}</span><span className="text-foreground font-mono">{v}</span></div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="license" className="mt-4">
          <LicenseTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
